/**
 * 报表数据处理 utils（v1.4 横版改造）
 *
 * 后端 /Report/regional 既保留旧 daquRows 树状结构，
 * 也额外返回 rows（已扁平化的门店行）。前端优先用 rows，
 * 否则从 daquRows 平铺。
 */

/**
 * 把 RegionalReportOutput 拍扁为门店行数组。
 * @param {Object} regionalData 后端响应
 * @param {Object} queryContext { businessType, year, month, quarter, businessTypeLabel }
 * @returns {Array}
 */
export function flattenStoreRows (regionalData, queryContext = {}) {
  if (!regionalData) return []

  // v1.5 修复 P0-03：只要后端返回了 rows 字段（不论是否为空），都以它为准；
  // 旧逻辑用 length>0 判断会让"筛选/分页后空结果"重新铺平 daquRows 全量树。
  if (Array.isArray(regionalData.rows)) {
    return regionalData.rows.map(r => ({
      ...r,
      businessType: r.businessType || queryContext.businessTypeLabel || queryContext.businessType,
      year: queryContext.year,
      month: queryContext.month,
      quarter: queryContext.quarter
    }))
  }

  // 兜底：仅在没有 rows 字段时（旧版后端响应）从 daquRows 树拍扁
  const stores = []
  ;(regionalData.daquRows || []).forEach(daqu => {
    ;(daqu.children || []).forEach(xiaoqu => {
      ;(xiaoqu.children || []).forEach(store => {
        stores.push({
          ...store,
          daquName: store.daquName || daqu.name,
          xiaoquName: store.xiaoquName || xiaoqu.name,
          businessType: store.businessType || queryContext.businessTypeLabel || queryContext.businessType,
          year: queryContext.year,
          month: queryContext.month,
          quarter: queryContext.quarter
        })
      })
    })
  })
  return stores
}

/**
 * 给一级 segmentColumns 按 sortOrder 升序生成字母编码 A/B/C...
 * 后端已生成时直接返回；本函数作为前端兜底。
 */
export function ensureLetterCodes (segmentColumns) {
  if (!Array.isArray(segmentColumns) || segmentColumns.length === 0) return []
  const top = segmentColumns
    .filter(s => s.level === 1 || s.Level === 1)
    .sort((a, b) => (a.sortOrder ?? a.SortOrder ?? 0) - (b.sortOrder ?? b.SortOrder ?? 0))
  top.forEach((seg, i) => {
    if (!seg.letterCode && !seg.LetterCode) {
      seg.letterCode = i < 26
        ? String.fromCharCode(65 + i)
        : 'A' + String.fromCharCode(65 + ((i - 26) % 26))
    }
  })
  return segmentColumns
}

/**
 * v1.5 修复 P1-01：把后端返回的 segmentColumns 归一化为：
 *   - topSegmentColumns: 仅 Level=1，且按 sortOrder 排序，已 ensure 字母
 *   - segmentByKey: { segmentKey: segment } 全量索引，用于查找父子
 *
 * 横版表头分组只用 topSegmentColumns；
 * 题项归到 topSegmentKey 下渲染（通过 itemColumns[*].topSegmentKey）。
 */
export function normalizeSegmentColumns (segmentColumns) {
  const all = Array.isArray(segmentColumns) ? segmentColumns : []
  ensureLetterCodes(all)
  const topSegmentColumns = all
    .filter(s => (s.level === 1 || s.Level === 1))
    .map(s => ({
      ...s,
      level: s.level ?? s.Level,
      sortOrder: s.sortOrder ?? s.SortOrder,
      letterCode: s.letterCode ?? s.LetterCode,
      segmentKey: s.segmentKey ?? s.SegmentKey,
      segmentName: s.segmentName ?? s.SegmentName,
      parentKey: s.parentKey ?? s.ParentKey
    }))
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
  const segmentByKey = {}
  all.forEach(s => {
    const key = s.segmentKey ?? s.SegmentKey
    if (key) segmentByKey[key] = s
  })
  return { topSegmentColumns, segmentByKey, allSegmentColumns: all }
}

/**
 * 期次显示名拼接
 */
export function buildPeriodLabel (reportType, year, month, quarter) {
  if (!year) return ''
  if (reportType === 1) return month ? `${year}年${month}月` : `${year}年`
  if (reportType === 2) return quarter ? `${year}年Q${quarter}` : `${year}年`
  if (reportType === 3) return `${year}年度`
  return `${year}年`
}

/**
 * 报告标题：业务类型-年度-期次-成绩
 */
export function buildReportTitle (businessTypeLabel, reportType, year, month, quarter) {
  const biz = businessTypeLabel || ''
  if (reportType === 1) return `${biz}-${year}-${month || ''}月-成绩`
  if (reportType === 2) return `${biz}-${year}-Q${quarter || ''}-成绩`
  if (reportType === 3) return `${biz}-${year}年度-成绩`
  return `${biz}-${year}-成绩`
}

/**
 * 把后端返回的字段名归一化为 camelCase（兼容 .NET PascalCase）
 * 现有调用如果传 PascalCase 就转，否则原样返回。
 */
export function normalizeReportPayload (payload) {
  if (!payload) return payload
  // 多数响应已是 camelCase（System.Text.Json + CamelCase）；
  // 但一些字段如 SegmentColumns 可能因匿名对象 dynamic 输出大小写不同。
  return payload
}
